import { inject, Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { AuthService } from '@auth0/auth0-angular';
import { ClaimKeys } from '../constants';
import { UserApiClient } from '../api-clients';
import { AuthStateService } from './auth-state-service';

@Injectable({
    providedIn: 'root',
})
export class AuthGuard implements CanActivate {
    private readonly _router = inject(Router);

    private readonly _authService = inject(AuthService);

    private readonly _userApiClient = inject(UserApiClient);

    private readonly _authState = inject(AuthStateService);

    canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        return this._authService.getAccessTokenSilently().pipe(
            catchError(err => {
                console.warn('Failed to get access token, now trying to redirect to login portal', err);
                return this._authService
                    .loginWithRedirect({
                        appState: {
                            target: state.url,
                        },
                    })
                    .pipe(map(() => null));
            }),
            switchMap(token => {
                if (token == null) return of(false);

                const jwtHelper = new JwtHelperService();
                const authData = jwtHelper.decodeToken(token);

                this._authState.setAuthData(authData);
                // console.log('Auth data:', authData); // TODO: DIT
                const registrationCompletedClaim = authData[ClaimKeys.registrationCompleted];

                const registrationCompleted: boolean = registrationCompletedClaim
                    ? JSON.parse(registrationCompletedClaim.toLowerCase())
                    : false;

                const emailVerified = authData[ClaimKeys.emailVerified];

                if (!emailVerified) {
                    // this._router.navigate(['/account/verify']);
                    this._router.createUrlTree(['/account/verify']);
                    return of(false);
                } else if (!registrationCompleted) {
                    return this._userApiClient.getBusiness().pipe(
                        map(() => {
                            this._router.navigate(['/account/profile']);
                            return false;
                        }),
                        catchError(() => {
                            this._router.navigate(['/account/profile']);
                            return of(false);
                        })
                    );
                } else {
                    return of(true);
                }
            }),
            shareReplay(1) // TODO: We might want to cache the result of this guard to avoid multiple calls during app initialization, but we need to make sure it doesn't cause issues with the login redirect flow. For now, we'll leave it out.
        );
    }
}
